import React, { useState, useEffect } from "react";
import { collection, getDocs, query, where, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import DashboardBase from "../DashboardBase";
import { getRolDisplay, hasSupeRadminPrivileges } from "../utils/roles";

const InformesManager = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluacionesCompletadas, setEvaluacionesCompletadas] = useState([]);
  const [plantillasInformes, setPlantillasInformes] = useState([]);
  const [informesGenerados, setInformesGenerados] = useState([]);
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null);
  const [selectedPlantillas, setSelectedPlantillas] = useState([]);
  const [showInformeEditor, setShowInformeEditor] = useState(false);
  const [currentInforme, setCurrentInforme] = useState({
    titulo: '',
    contenido: '',
    evaluacionId: '',
    plantillaId: '',
    estado: 'borrador'
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        loadUserData(user);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (user) => {
    try {
      console.log("🔍 Usuario Firebase:", user.email);
      
      // Si es uno de los usuarios con privilegios especiales de superadmin
      if (hasSupeRadminPrivileges(user.email)) {
        console.log("🎯 Detectado ADMIN PRINCIPAL - Forzando rol superadmin");
        const adminData = {
          uid: user.uid,
          email: user.email,
          nombre: user.email === "db@ossinnovation.com" ? "basededatos" : "Administrador",
          apellido: user.email === "db@ossinnovation.com" ? "datos" : "Sistema", 
          rol: "superadmin",
          estado: "activo",
          fechaCreacion: new Date()
        };
        console.log("✅ ACCESO AUTORIZADO - Admin principal con rol superadmin");
        setUserData(adminData);
        
        await Promise.all([
          loadEvaluacionesCompletadas(adminData),
          loadPlantillasInformes(),
          loadInformesGenerados(adminData)
        ]);
        return;
      }
      
      const q = query(collection(db, "usuarios"), where("uid", "==", user.uid));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = { id: userDoc.id, ...userDoc.data() };
        setUserData(userData);
        
        await Promise.all([
          loadEvaluacionesCompletadas(userData),
          loadPlantillasInformes(),
          loadInformesGenerados(userData)
        ]);
      }
    } catch (error) {
      console.error("Error al cargar datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvaluacionesCompletadas = async (userData) => {
    try {
      console.log("🔍 Cargando evaluaciones completadas para empresa:", userData.empresaId);
      const q = query(
        collection(db, "evaluaciones"),
        where("empresaId", "==", userData.empresaId),
        where("estado", "==", "completada")
      );
      
      const querySnapshot = await getDocs(q);
      const evaluaciones = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("📊 Evaluaciones completadas:", evaluaciones.length);
      setEvaluacionesCompletadas(evaluaciones);
    } catch (error) {
      console.error("Error al cargar evaluaciones completadas:", error);
    }
  };

  const loadPlantillasInformes = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "plantillas_informes"));
      const plantillas = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("📋 Plantillas de informes:", plantillas.length);
      setPlantillasInformes(plantillas);
    } catch (error) {
      console.error("Error al cargar plantillas de informes:", error);
    }
  };

  const loadInformesGenerados = async (userData) => {
    try {
      const q = query(
        collection(db, "informes"),
        where("empresaId", "==", userData.empresaId),
        where("psicologoId", "==", userData.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const informes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log("📝 Informes generados:", informes.length);
      setInformesGenerados(informes);
    } catch (error) {
      console.error("Error al cargar informes generados:", error);
    }
  };

  const handleGenerarInforme = (evaluacion, plantilla) => {
    setCurrentInforme({
      titulo: `${plantilla.nombre} - ${evaluacion.evaluadoNombre || 'Evaluación'}`,
      contenido: plantilla.contenido || '',
      evaluacionId: evaluacion.id,
      plantillaId: plantilla.id,
      evaluadoNombre: evaluacion.evaluadoNombre,
      fechaEvaluacion: evaluacion.fechaCreacion,
      estado: 'borrador'
    });
    setShowInformeEditor(true);
  };

  const handleSaveInforme = async () => {
    try {
      const informeData = {
        ...currentInforme,
        empresaId: userData.empresaId,
        psicologoId: userData.uid,
        psicologoNombre: `${userData.nombre} ${userData.apellido}`,
        fechaCreacion: new Date(),
        fechaActualizacion: new Date()
      };

      if (currentInforme.id) {
        // Actualizar informe existente
        await updateDoc(doc(db, "informes", currentInforme.id), informeData);
        console.log("✅ Informe actualizado");
      } else {
        // Crear nuevo informe
        await addDoc(collection(db, "informes"), informeData);
        console.log("✅ Informe creado");
      }

      await loadInformesGenerados(userData);
      setShowInformeEditor(false);
      setCurrentInforme({
        titulo: '',
        contenido: '',
        evaluacionId: '',
        plantillaId: '',
        estado: 'borrador'
      });
    } catch (error) {
      console.error("Error al guardar informe:", error);
      alert("Error al guardar el informe");
    }
  };

  const getRolDisplay = (rol) => {
    const roles = {
      'superadmin': 'Super Administrador',
      'admin': 'Administrador', 
      'rrhh': 'RRHH',
      'psicologo': 'Psicólogo'
    };
    return roles[rol] || rol;
  };

  if (loading) {
    return (
      <DashboardBase 
        title="Cargando..." 
        activeNav="informes"
        userName={userData ? `${userData.nombre} ${userData.apellido}` : "Cargando..."}
        userRole={userData?.rol || ""}
        userRoleDisplay={userData ? getRolDisplay(userData.rol) : "Cargando..."}
      >
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Cargando informes...</p>
        </div>
      </DashboardBase>
    );
  }

  // Solo psicólogos y administradores pueden acceder
  if (!['admin', 'rrhh', 'superadmin', 'psicologo'].includes(userData?.rol)) {
    return (
      <DashboardBase 
        title="Acceso Restringido" 
        activeNav="informes"
        userName={userData ? `${userData.nombre} ${userData.apellido}` : "Usuario"}
        userRole={userData?.rol || ""}
        userRoleDisplay={userData ? getRolDisplay(userData.rol) : "Sin rol"}
      >
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
          <h2>Acceso Restringido</h2>
          <p>Solo los psicólogos y personal administrativo pueden gestionar informes.</p>
        </div>
      </DashboardBase>
    );
  }

  return (
    <DashboardBase 
      title="Gestión de Informes Psicológicos" 
      activeNav="informes"
      userName={userData ? `${userData.nombre} ${userData.apellido}` : "Usuario"}
      userRole={userData?.rol || ""}
      userRoleDisplay={userData ? getRolDisplay(userData.rol) : "Sin rol"}
    >
      <div style={{ padding: '1.5rem' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
          color: 'white',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem' }}>
            🧠 Informes Psicológicos
          </h1>
          <p style={{ margin: 0, opacity: 0.9 }}>
            Genera y gestiona informes psicológicos basados en evaluaciones completadas
          </p>
        </div>

        {/* Evaluaciones Completadas */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
            Evaluaciones Completadas ({evaluacionesCompletadas.length})
          </h3>
          
          {evaluacionesCompletadas.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#6b7280',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '2px dashed #d1d5db'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>No hay evaluaciones completadas</h4>
              <p style={{ margin: 0 }}>
                Las evaluaciones completadas aparecerán aquí para generar informes
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '1rem'
            }}>
              {evaluacionesCompletadas.map(evaluacion => (
                <div
                  key={evaluacion.id}
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}
                >
                  <div style={{ marginBottom: '0.5rem' }}>
                    <h4 style={{ margin: 0, color: '#1e293b' }}>
                      {evaluacion.evaluadoNombre || 'Evaluación'}
                    </h4>
                    <p style={{ margin: '0.25rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
                      {evaluacion.plantillaNombre || 'Plantilla no especificada'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      Completada
                    </span>
                  </div>
                  
                  {/* Botones para generar informes */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {plantillasInformes.slice(0, 2).map(plantilla => (
                      <button
                        key={plantilla.id}
                        onClick={() => handleGenerarInforme(evaluacion, plantilla)}
                        style={{
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        {plantilla.icono} {plantilla.nombre}
                      </button>
                    ))}
                    {plantillasInformes.length > 2 && (
                      <button
                        onClick={() => setSelectedEvaluacion(evaluacion)}
                        style={{
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        +{plantillasInformes.length - 2} más
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Informes Generados */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
            Mis Informes ({informesGenerados.length})
          </h3>
          
          {informesGenerados.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
              <p>No has generado informes aún</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem'
            }}>
              {informesGenerados.map(informe => (
                <div
                  key={informe.id}
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>
                    {informe.titulo}
                  </h4>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.875rem' }}>
                    {informe.evaluadoNombre}
                  </p>
                  <div style={{ marginBottom: '1rem' }}>
                    <span style={{
                      backgroundColor: informe.estado === 'finalizado' ? '#dcfce7' : '#fef3c7',
                      color: informe.estado === 'finalizado' ? '#166534' : '#92400e',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      {informe.estado === 'finalizado' ? 'Finalizado' : 'Borrador'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setCurrentInforme(informe);
                        setShowInformeEditor(true);
                      }}
                      style={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Editor de Informes */}
      {showInformeEditor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90%',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Editor de Informe</h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Título del Informe
              </label>
              <input
                type="text"
                value={currentInforme.titulo}
                onChange={(e) => setCurrentInforme({...currentInforme, titulo: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Contenido del Informe
              </label>
              <textarea
                value={currentInforme.contenido}
                onChange={(e) => setCurrentInforme({...currentInforme, contenido: e.target.value})}
                rows={15}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Estado
              </label>
              <select
                value={currentInforme.estado}
                onChange={(e) => setCurrentInforme({...currentInforme, estado: e.target.value})}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                <option value="borrador">Borrador</option>
                <option value="finalizado">Finalizado</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInformeEditor(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveInforme}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#059669',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Guardar Informe
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardBase>
  );
};

export default InformesManager;
